/**
 * aiCourseQuerySuggester.js
 * --------------------------------------------------------------------------
 * OPTIONAL LLM-assisted free-course query generator.
 *
 * Given a missing skill, asks Claude for 2-3 search queries that would
 * find good free learning content. The output is NOT clickable course
 * links from the LLM, it's search queries. We then render each query as
 * a deep-link to Class Central / Coursera / YouTube / etc., so even if
 * Claude hallucinates a course name, the user lands on a REAL search
 * results page on a REAL platform with REAL current courses.
 *
 * No fabricated course names, no fabricated URLs, no broken links.
 *
 * Same gating + warnings as aiSkillEnricher, direct browser call exposes
 * the API key (DEMO ONLY). Move to a Vercel function before public launch.
 */

import { logAiEvent } from './aiAuditLog';

const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';
const MODEL = 'claude-opus-4-7';

export function isLlmCourseSuggesterAvailable() {
  const k = import.meta.env?.VITE_ANTHROPIC_API_KEY;
  if (!k) return false;
  if (k.includes('your-') || k.includes('xxxx') || k.length < 20) return false;
  return true;
}

/**
 * Get search-query suggestions for a list of missing skills.
 * @param {Array<{ id: string, label: string }>} missingSkills
 * @returns {Promise<Object<string, Array<{ query: string, why: string }>>>}
 *   Map of skillId → suggestions
 */
export async function suggestCourseQueriesForSkills(missingSkills) {
  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;
  if (!apiKey || apiKey.includes('your-') || apiKey.includes('xxxx') || apiKey.length < 20 || !missingSkills?.length) {
    return {};
  }

  // Cap to top 6 missing skills to keep cost/latency bounded
  const skills = missingSkills.slice(0, 6);

  const systemPrompt = `You are a learning-pathway assistant for a pay-transparency tool.
You suggest SEARCH QUERIES (not course names, not URLs) that a worker can use to find free public learning content for skills they want to develop.
Rules:
1. Output ONLY search queries (3-7 words each). Do NOT name specific courses, instructors, or providers.
2. Do NOT include URLs.
3. Each query should be specific enough to find quality content but generic enough that real free courses on Coursera, edX, YouTube, or similar will exist.
4. Output strict JSON. No preamble.
5. Prefer queries that work across multiple providers (e.g. "negotiation skills training" works on Coursera, edX, YouTube, Class Central).`;

  const skillsList = skills.map((s) => `- id: "${s.id}", label: "${s.label}"`).join('\n');

  const userPrompt = `For each skill below, suggest exactly 3 search queries a worker could use to find free public learning content (free-to-audit courses, YouTube tutorials, MOOCs).

Skills:
${skillsList}

Return JSON with this exact shape:
{
  "suggestions": {
    "skillId1": [
      { "query": "search query text", "why": "one short sentence" },
      ...
    ],
    "skillId2": [...]
  }
}

Each query must be 3-7 words. Each "why" must be one sentence (max 20 words). Use the skill IDs given above as keys.`;

  const startTime = Date.now();
  try {
    const response = await fetch(ANTHROPIC_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: MODEL,
        max_tokens: 2048,
        system: systemPrompt,
        messages: [{ role: 'user', content: userPrompt }],
      }),
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      console.warn('Course query suggester: API error', response.status, errText.slice(0, 200));
      return {};
    }

    const data = await response.json();
    const text = data?.content?.[0]?.text || '';
    const cleaned = text.replace(/```json|```/g, '').trim();

    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch {
      console.warn('Course query suggester: parse error');
      return {};
    }

    const suggestions = parsed?.suggestions || {};

    // Sanitise: only keep entries with id we asked about, queries that
    // are reasonable strings, etc.
    const validIds = new Set(skills.map((s) => s.id));
    const cleanedSuggestions = {};
    for (const [id, list] of Object.entries(suggestions)) {
      if (!validIds.has(id)) continue;
      if (!Array.isArray(list)) continue;
      cleanedSuggestions[id] = list
        .filter((x) => x && typeof x.query === 'string' && x.query.length > 0 && x.query.length < 120)
        .slice(0, 3)
        .map((x) => ({
          query: x.query.trim(),
          why: typeof x.why === 'string' ? x.why.slice(0, 200) : '',
        }));
    }

    logAiEvent({
      operation: 'ai_insight',
      kind: 'course_query_suggestions',
      skillsRequested: skills.length,
      skillsAnswered: Object.keys(cleanedSuggestions).length,
      durationMs: Date.now() - startTime,
    });

    return cleanedSuggestions;
  } catch (err) {
    console.warn('Course query suggester: network error', err);
    return {};
  }
}
